import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, Factory, Award, Truck, Shield, Phone, Zap, Package, Users, Cable, BatteryCharging } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FastChargingCableManufacturer = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const chargingProtocols = [
    { name: 'Qualcomm Quick Charge', versions: 'QC 2.0, 3.0, 4.0, 5.0', power: 'Up to 100W', compatible: 'Xiaomi, Samsung, LG, Motorola' },
    { name: 'USB Power Delivery (PD)', versions: 'PD 2.0, PD 3.0, PD 3.1', power: 'Up to 240W', compatible: 'iPhone, Samsung, Laptops, Universal' },
    { name: 'VOOC / Super VOOC', versions: '33W, 65W, 80W, 120W', power: 'Up to 120W', compatible: 'OPPO, Realme, OnePlus (newer)' },
    { name: 'Dash / Warp Charging', versions: 'Dash 30W, Warp 65W', power: 'Up to 65W', compatible: 'OnePlus Devices' },
    { name: 'Super Fast Charging', versions: '25W, 45W', power: 'Up to 45W', compatible: 'Samsung Galaxy Series' },
    { name: 'Apple Fast Charging', versions: '20W PD, 30W PD', power: 'Up to 30W', compatible: 'iPhone 8 and later, iPad' }
  ];

  const cableSeries = [
    { series: 'DT-13 Series', power: '45W', type: 'Silicone', features: ['Soft Touch', 'Flexible', 'Tangle-Free'] },
    { series: 'DT-15 Series', power: '65W', type: 'Braided Nylon', features: ['Premium Build', 'VOOC Support', 'Metal Connectors'] },
    { series: 'DT-21 Series', power: '100W', type: 'Premium', features: ['Super Fast', 'LED Indicator', 'E-Marker Chip'] },
    { series: 'DT-25 Series', power: '120W', type: 'Gaming Grade', features: ['Max Power', '180° Rotating', 'RGB Option'] }
  ];

  const faqs = [
    {
      q: 'Who is the best fast charging cable manufacturer in India?',
      a: 'RSD Accessories Pvt Ltd is a leading fast charging cable manufacturer in India. Based in Noida, we specialize in manufacturing cables supporting 45W to 120W fast charging. Our cables support all major protocols including QC, PD, VOOC, and Warp Charging. With ISO 9001:2015 certification and 30,000+ daily cable production, RSD is trusted by 500+ dealers across India.'
    },
    {
      q: 'What fast charging protocols do RSD cables support?',
      a: 'RSD cables support all major fast charging protocols: Qualcomm Quick Charge (2.0-5.0), USB Power Delivery (PD 3.0/3.1), VOOC/Super VOOC (33W-120W), Dash/Warp Charging (30W-65W), Samsung Super Fast Charging (25W-45W), and Apple 20W/30W PD fast charging. Each cable clearly specifies supported protocols.'
    },
    {
      q: 'What is the maximum wattage RSD cables support?',
      a: 'RSD manufactures fast charging cables from 45W to 120W. Our DT-13 series supports 45W, DT-15 series supports 65W VOOC, DT-21 series supports 100W PD, and our premium DT-25 series supports up to 120W max power. We also manufacture 240W USB4 cables for specific requirements.'
    },
    {
      q: 'What types of fast charging cables does RSD manufacture?',
      a: 'RSD manufactures: USB-A to Type-C (45W-120W), USB-A to Micro USB (QC 3.0), Type-C to Type-C (60W-240W PD), Type-C to Lightning (20W-30W PD for iPhone). Available in silicone, braided nylon, and premium builds with options like LED indicators and 180° rotating heads.'
    },
    {
      q: 'How does RSD ensure cable quality for fast charging?',
      a: 'RSD ensures quality through: pure copper core wiring for optimal power transfer, 10,000+ bend cycle durability testing, fast charging protocol verification testing, E-Marker chip integration for high-wattage cables, temperature testing during charging, and ISO 9001:2015 quality management system.'
    },
    {
      q: 'Can RSD manufacture custom fast charging cables?',
      a: 'Yes, RSD provides OEM/ODM services for custom fast charging cables. We offer: custom branding and logo, custom cable lengths (1m, 1.5m, 2m, 3m), custom colors and materials, specific protocol optimization, and private label packaging. MOQ starts from 5,000 units.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Fast Charging Cable Manufacturer in India | RSD Accessories - 45W to 120W Cables</title>
        <meta name="description" content="RSD Accessories - India's leading fast charging cable manufacturer. 45W-120W cables. QC, PD, VOOC, Warp support. ISO certified. OEM manufacturing. Wholesale prices for distributors." />
        <meta name="keywords" content="fast charging cable manufacturer, quick charge cable, vooc cable manufacturer, pd cable manufacturer, 100w cable manufacturer, 120w cable india, fast charging cable wholesale, bulk charging cables" />
        <link rel="canonical" href="https://rsdaccessories.com/fast-charging-cable-manufacturer" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-black py-20 md:py-28">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23FF4D00" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <span className="inline-block bg-[#FF4D00] text-white px-4 py-1 rounded-full text-sm font-semibold mb-6">
                45W to 120W Fast Charging
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
                Fast Charging Cable Manufacturer in <span className="text-[#FF4D00]">India</span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-300 max-w-3xl mx-auto mb-8">
                RSD Accessories - QC, PD, VOOC, Warp Charging Support | 30,000+ Cables Daily | ISO Certified Factory
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-[#FF4D00] hover:bg-[#E64500] text-white px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Request Bulk Quote
                  </Button>
                </Link>
                <Link to="/shop/data-cables">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg">
                    View Cable Range
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Power Range Banner */}
        <section className="py-8 bg-gradient-to-r from-[#FF4D00] to-orange-600">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-wrap justify-center items-center gap-8 text-white">
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6" />
                <span className="font-bold">45W</span>
              </div>
              <span className="text-white/50">→</span>
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6" />
                <span className="font-bold">65W VOOC</span>
              </div>
              <span className="text-white/50">→</span>
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6" />
                <span className="font-bold">100W PD</span>
              </div>
              <span className="text-white/50">→</span>
              <div className="flex items-center gap-2">
                <BatteryCharging className="w-6 h-6" />
                <span className="font-bold">120W MAX</span>
              </div>
            </div>
          </div>
        </section>

        {/* Charging Protocols */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                Fast Charging Protocols We Support
              </h2>
              <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                Our cables are engineered to support all major fast charging technologies
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {chargingProtocols.map((protocol, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-lg border border-neutral-100 hover:border-[#FF4D00] transition-colors">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="w-8 h-8 text-[#FF4D00]" />
                    <h3 className="text-xl font-bold text-neutral-900">{protocol.name}</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-semibold">Versions:</span> {protocol.versions}</p>
                    <p><span className="font-semibold">Power:</span> <span className="text-[#FF4D00] font-bold">{protocol.power}</span></p>
                    <p><span className="font-semibold">Compatible:</span> {protocol.compatible}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cable Series */}
        <section className="py-16 bg-neutral-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Fast Charging Cable Series
              </h2>
              <p className="text-neutral-400">Premium cables for every power requirement</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cableSeries.map((cable, idx) => (
                <div key={idx} className="bg-neutral-800 rounded-xl p-6 hover:bg-neutral-700 transition-colors">
                  <Cable className="w-10 h-10 text-[#FF4D00] mb-4" />
                  <h3 className="text-xl font-bold mb-2">{cable.series}</h3>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-[#FF4D00] px-3 py-1 rounded-full text-sm font-bold">{cable.power}</span>
                    <span className="text-neutral-400 text-sm">{cable.type}</span>
                  </div>
                  <ul className="space-y-2">
                    {cable.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-neutral-300">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Fast Charging Cables Matter */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
                  Why Quality Fast Charging Cables Matter
                </h2>
                <p className="text-lg text-neutral-600 mb-6">
                  Not all cables can handle fast charging. Cheap cables can limit charging speed, generate heat, or even damage devices. RSD fast charging cables are engineered with:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FF4D00] mt-1" />
                    <div>
                      <h3 className="font-bold">Pure Copper Core</h3>
                      <p className="text-neutral-600 text-sm">Optimal power transfer with minimal resistance</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FF4D00] mt-1" />
                    <div>
                      <h3 className="font-bold">E-Marker Chip (100W+)</h3>
                      <p className="text-neutral-600 text-sm">Smart chip for safe high-wattage charging</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FF4D00] mt-1" />
                    <div>
                      <h3 className="font-bold">Protocol Optimization</h3>
                      <p className="text-neutral-600 text-sm">Cables tuned for specific charging protocols</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FF4D00] mt-1" />
                    <div>
                      <h3 className="font-bold">10,000+ Bend Cycles</h3>
                      <p className="text-neutral-600 text-sm">Durability tested for long-term reliability</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#FF4D00] to-orange-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Manufacturing Excellence</h3>
                <ul className="space-y-4">
                  <li className="flex items-center gap-3">
                    <Factory className="w-6 h-6" />
                    <span>30,000+ cables produced daily</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Award className="w-6 h-6" />
                    <span>ISO 9001:2015 certified facility</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Shield className="w-6 h-6" />
                    <span>100% quality tested products</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Users className="w-6 h-6" />
                    <span>500+ dealer network</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Truck className="w-6 h-6" />
                    <span>Pan India delivery 3-5 days</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-neutral-600">About fast charging cable manufacturing</p>
            </div>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow border border-neutral-200">
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">{faq.q}</h3>
                  <p className="text-neutral-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Source Fast Charging Cables from India's Best
            </h2>
            <p className="text-xl text-white/90 mb-8">
              45W to 120W cables | All protocols supported | Competitive B2B pricing
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: +91-9311211889
                </Button>
              </Link>
              <Link to="/become-distributor">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00] px-8 py-6 text-lg">
                  Become a Distributor
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FastChargingCableManufacturer;
